/**
 * Lottery theming (§13.3): organizer accent → action/focus, hero prize accent → prize-accent, presenting
 * sponsor colour → sponsor-accent, each only after the kit's contrast check; failed colours fall back to
 * approved neutrals and the fallback list is reported (console warning in dev, returned to the caller).
 */
import { useEffect, useState } from 'react';
import type { PublicLotteryDto, PublicPrizeDto, PublicSponsorDto } from '@windrop/contracts';
import {
  applyTheme,
  clearTheme,
  relativeLuminance,
  type ResolvedTheme,
  type ThemeInput,
  type ThemeMode,
} from '@windrop/ui';

const TIER_ORDER: Record<PublicPrizeDto['tier'], number> = {
  GRAND: 0,
  MAJOR: 1,
  MINOR: 2,
  SPONSOR_SAMPLE: 3,
};

/** The prize shown in the hero: highest tier, then highest value. */
export function heroPrize(lottery: Pick<PublicLotteryDto, 'prizes'>): PublicPrizeDto | undefined {
  return [...lottery.prizes].sort((a, b) => {
    const t = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
    if (t !== 0) return t;
    return BigInt(b.value.amountMinor) > BigInt(a.value.amountMinor) ? 1 : -1;
  })[0];
}

/** The presenting (TITLE) sponsor, by tier then display weight. */
export function titleSponsor(lottery: Pick<PublicLotteryDto, 'sponsors'>): PublicSponsorDto | undefined {
  const order: Record<PublicSponsorDto['tier'], number> = { TITLE: 0, GOLD: 1, SUPPORTING: 2 };
  return [...lottery.sponsors].sort(
    (a, b) => order[a.tier] - order[b.tier] || b.displayWeight - a.displayWeight,
  )[0];
}

export function themeMode(lottery: Pick<PublicLotteryDto, 'presentation'>): ThemeMode {
  try {
    return relativeLuminance(lottery.presentation.theme.canvas) > 0.5 ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function themeInputFromLottery(lottery: PublicLotteryDto): ThemeInput {
  const prize = heroPrize(lottery);
  const sponsor = titleSponsor(lottery);
  const { theme, prizeAccents } = lottery.presentation;
  return {
    organizerAccent: theme.action,
    prizeAccent: (prize && (prizeAccents[prize.prizeId] ?? prize.accentColor)) ?? theme.prizeAccent,
    sponsorAccent: sponsor?.brandColor ?? theme.sponsorAccent,
    mode: themeMode(lottery),
  };
}

export interface AppliedTheme extends ResolvedTheme {
  /** Human-readable fallback report (empty when every brand colour passed). */
  report: string[];
}

export function applyLotteryTheme(lottery: PublicLotteryDto, root?: HTMLElement | null): AppliedTheme {
  const input = themeInputFromLottery(lottery);
  const theme = applyTheme(root ? { ...input, root } : input);
  const report = theme.fallbacks.map(
    (f) => `${f.role}: brand colour rejected (${f.reason ?? `ratio ${f.ratio.toFixed(2)}`}) → ${f.value}`,
  );
  if (report.length && import.meta.env.DEV)
    console.warn('[windrop] theme fallbacks for', lottery.slug, report);
  return { ...theme, report };
}

/** Applies the lottery theme on mount / change and clears it on unmount. Returns the resolved theme. */
export function useLotteryTheme(lottery: PublicLotteryDto | undefined): AppliedTheme | null {
  const [applied, setApplied] = useState<AppliedTheme | null>(null);
  useEffect(() => {
    if (!lottery) return;
    setApplied(applyLotteryTheme(lottery));
    return () => {
      clearTheme();
      setApplied(null);
    };
  }, [lottery]);
  return applied;
}
